/* ===========================================================
   music/visualizers.js — the visualizer library (his plan: "a library
   of visualizers they can choose from, like really cool ones")

   Each one draws a frame from the sound: freq (0-255 per band, low to
   high) and wave (the waveform, 128 = silence). They keep their own
   little state between frames.
   =========================================================== */

const avg = (a, from, to) => { let s = 0; for (let i = from; i < to; i++) s += a[i]; return s / Math.max(1, to - from) / 255; };

function bars(c, w, h, f, _wave, s, col) {
  const n = 64, bw = w / n;
  s.peaks = s.peaks || [];
  c.fillStyle = 'rgba(0,0,0,0.25)'; c.fillRect(0, 0, w, h);
  for (let i = 0; i < n; i++) {
    const v = f[Math.floor((i / n) ** 1.6 * f.length * 0.85)] / 255;
    s.peaks[i] = Math.max((s.peaks[i] || 0) - 0.008, v);
    const bh = v * h * 0.9;
    const g = c.createLinearGradient(0, h, 0, h - bh);
    g.addColorStop(0, col.a); g.addColorStop(1, col.b);
    c.fillStyle = g;
    c.fillRect(i * bw + 1, h - bh, bw - 2, bh);
    c.fillStyle = col.fg;
    c.fillRect(i * bw + 1, h - s.peaks[i] * h * 0.9 - 3, bw - 2, 2);
  }
}

function wave(c, w, h, _f, wv, s, col) {
  c.fillStyle = 'rgba(0,0,0,0.18)'; c.fillRect(0, 0, w, h);
  c.lineWidth = 3; c.strokeStyle = col.a; c.shadowColor = col.a; c.shadowBlur = 14;
  c.beginPath();
  for (let i = 0; i < wv.length; i++) {
    const x = i / (wv.length - 1) * w, y = h / 2 + (wv[i] - 128) / 128 * h * 0.42;
    i ? c.lineTo(x, y) : c.moveTo(x, y);
  }
  c.stroke(); c.shadowBlur = 0;
}

function halo(c, w, h, f, _wv, s, col) {
  c.fillStyle = 'rgba(0,0,0,0.22)'; c.fillRect(0, 0, w, h);
  const cx = w / 2, cy = h / 2, base = Math.min(w, h) * 0.2, bass = avg(f, 0, 8);
  s.rot = (s.rot || 0) + 0.002 + bass * 0.01;
  const n = 120;
  for (let i = 0; i < n; i++) {
    const v = f[Math.floor(i / n * f.length * 0.7)] / 255;
    const a = i / n * Math.PI * 2 + s.rot, r1 = base * (1 + bass * 0.25), r2 = r1 + v * Math.min(w, h) * 0.28;
    c.strokeStyle = i % 2 ? col.a : col.b; c.lineWidth = 2.2;
    c.beginPath(); c.moveTo(cx + Math.cos(a) * r1, cy + Math.sin(a) * r1); c.lineTo(cx + Math.cos(a) * r2, cy + Math.sin(a) * r2); c.stroke();
  }
  c.beginPath(); c.arc(cx, cy, base * (0.9 + bass * 0.3), 0, Math.PI * 2);
  c.fillStyle = col.a; c.globalAlpha = 0.05 + bass * 0.35; c.fill(); c.globalAlpha = 1;
}

function particles(c, w, h, f, _wv, s, col) {
  c.fillStyle = 'rgba(0,0,0,0.2)'; c.fillRect(0, 0, w, h);
  s.p = s.p || [];
  const bass = avg(f, 0, 6), mid = avg(f, 10, 60);
  const spawn = Math.floor(2 + bass * 14);
  for (let i = 0; i < spawn && s.p.length < 600; i++) {
    const a = Math.random() * Math.PI * 2, sp = 1 + bass * 7 * Math.random();
    s.p.push({ x: w / 2, y: h / 2, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 1, hue: Math.random() < 0.5 ? col.a : col.b, r: 1 + Math.random() * 2.5 });
  }
  for (const p of s.p) {
    p.x += p.vx * (1 + mid); p.y += p.vy * (1 + mid); p.life -= 0.012;
    c.globalAlpha = Math.max(0, p.life); c.fillStyle = p.hue;
    c.beginPath(); c.arc(p.x, p.y, p.r * (1 + bass), 0, Math.PI * 2); c.fill();
  }
  c.globalAlpha = 1;
  s.p = s.p.filter(p => p.life > 0 && p.x > -20 && p.x < w + 20 && p.y > -20 && p.y < h + 20);
}

/* Weather Protocol's own: rain that falls harder with the music, lightning on the big beats */
function storm(c, w, h, f, _wv, s, col) {
  const bass = avg(f, 0, 6), hi = avg(f, 60, 200);
  c.fillStyle = 'rgba(4,10,20,0.35)'; c.fillRect(0, 0, w, h);
  s.drops = s.drops || [];
  const want = 80 + Math.floor(hi * 500);
  while (s.drops.length < want) s.drops.push({ x: Math.random() * w, y: Math.random() * -h, v: 6 + Math.random() * 8, l: 8 + Math.random() * 14 });
  s.drops.length = Math.min(s.drops.length, want + 40);
  c.strokeStyle = 'rgba(160,200,255,0.55)'; c.lineWidth = 1.3;
  c.beginPath();
  for (const d of s.drops) {
    d.y += d.v * (1 + hi * 2); d.x -= 1.2;
    if (d.y > h) { d.y = -d.l; d.x = Math.random() * (w + 60); }
    c.moveTo(d.x, d.y); c.lineTo(d.x - 2, d.y + d.l);
  }
  c.stroke();
  // lightning when the bass jumps
  s.prev = s.prev ?? bass;
  if (bass - s.prev > 0.12 && bass > 0.55 && (!s.flash || s.flash < 0.2)) {
    s.flash = 1;
    let x = Math.random() * w, y = 0;
    s.bolt = [[x, y]];
    while (y < h * 0.8) { x += (Math.random() - 0.5) * 60; y += 20 + Math.random() * 40; s.bolt.push([x, y]); }
  }
  s.prev = s.prev * 0.8 + bass * 0.2;
  if (s.flash > 0) {
    c.fillStyle = `rgba(220,235,255,${s.flash * 0.25})`; c.fillRect(0, 0, w, h);
    c.strokeStyle = `rgba(255,255,255,${s.flash})`; c.lineWidth = 3; c.shadowColor = col.b; c.shadowBlur = 20;
    c.beginPath(); s.bolt.forEach(([x, y], i) => (i ? c.lineTo(x, y) : c.moveTo(x, y))); c.stroke(); c.shadowBlur = 0;
    s.flash -= 0.06;
  }
}

function tunnel(c, w, h, f, _wv, s, col) {
  c.fillStyle = 'rgba(0,0,0,0.15)'; c.fillRect(0, 0, w, h);
  s.t = (s.t || 0) + 0.02 + avg(f, 0, 8) * 0.06;
  const cx = w / 2, cy = h / 2;
  for (let k = 0; k < 14; k++) {
    const z = ((k + s.t * 3) % 14) / 14, r = z * z * Math.max(w, h) * 0.8;
    const v = f[Math.floor(k / 14 * 120)] / 255;
    c.strokeStyle = k % 2 ? col.a : col.b; c.globalAlpha = 0.15 + z * 0.85; c.lineWidth = 1 + v * 6 * z;
    c.beginPath();
    const sides = 6;
    for (let i = 0; i <= sides; i++) {
      const a = i / sides * Math.PI * 2 + s.t * (k % 2 ? 1 : -1) * 0.5;
      const x = cx + Math.cos(a) * r, y = cy + Math.sin(a) * r;
      i ? c.lineTo(x, y) : c.moveTo(x, y);
    }
    c.stroke();
  }
  c.globalAlpha = 1;
}

export const VISUALIZERS = [
  { id: 'bars', name: 'Bars', draw: bars },
  { id: 'halo', name: 'Halo', draw: halo },
  { id: 'storm', name: 'Storm', draw: storm },
  { id: 'particles', name: 'Particles', draw: particles },
  { id: 'wave', name: 'Wave', draw: wave },
  { id: 'tunnel', name: 'Tunnel', draw: tunnel },
];

/** "[01:23.45] words" → [{ t: 83.45, text }] in time order. */
export function parseLRC(lrc) {
  const out = [];
  for (const line of String(lrc || '').split('\n')) {
    const times = [...line.matchAll(/\[(\d{1,2}):(\d{1,2}(?:\.\d{1,3})?)\]/g)];
    if (!times.length) continue;
    const text = line.replace(/\[[^\]]*\]/g, '').trim();
    for (const m of times) out.push({ t: Number(m[1]) * 60 + Number(m[2]), text });
  }
  return out.sort((a, b) => a.t - b.t);
}
